// src/pages/Reports.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart2, TrendingUp, Users2, Server, Globe, DollarSign,
  Download, RefreshCw, Calendar, Activity, CheckCircle, XCircle,
  ArrowUpRight, ArrowDownRight, Filter, AlertTriangle
} from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';

// ─── SVG Bar Chart ──────────────────────────────────────────────────────────
function BarChart({ data, labelKey, valueKey, color = 'var(--accent)', height = 160, title }) {
  if (!data || data.length === 0) return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
      Nu există date
    </div>
  );
  const max = Math.max(...data.map(d => d[valueKey]), 1);
  const W = 400, H = height;
  const PAD = { top: 12, right: 8, bottom: 32, left: 32 };
  const barW = Math.max(((W - PAD.left - PAD.right) / data.length) - 6, 8);

  return (
    <div>
      {title && <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>}
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', overflow: 'visible' }}>
        {/* Y grid */}
        {[0, 0.5, 1].map(pct => {
          const y = PAD.top + (1 - pct) * (H - PAD.top - PAD.bottom);
          return (
            <g key={pct}>
              <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y} stroke="var(--border)" strokeWidth="1" strokeDasharray="3,3" />
              <text x={PAD.left - 4} y={y + 4} textAnchor="end" fill="var(--text-secondary)" fontSize="9">{Math.round(max * pct)}</text>
            </g>
          );
        })}
        {/* Bars */}
        {data.map((d, i) => {
          const x = PAD.left + i * ((W - PAD.left - PAD.right) / data.length) + 3;
          const barH = Math.max(((d[valueKey] || 0) / max) * (H - PAD.top - PAD.bottom), 2);
          const y = H - PAD.bottom - barH;
          const label = String(d[labelKey] || '').substring(0, 8);
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={barH} fill={color} rx="3" opacity="0.85" />
              <text x={x + barW / 2} y={H - PAD.bottom + 12} textAnchor="middle" fill="var(--text-secondary)" fontSize="9">{label}</text>
              <text x={x + barW / 2} y={y - 4} textAnchor="middle" fill="var(--text-secondary)" fontSize="9">{d[valueKey]}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── SVG Donut Chart ────────────────────────────────────────────────────────
function DonutChart({ segments, size = 120 }) {
  const total = segments.reduce((s, seg) => s + (seg.value || 0), 0);
  if (total === 0) return null;

  const cx = size / 2, cy = size / 2, r = size * 0.38, inner = size * 0.24;
  let angle = -Math.PI / 2;

  const paths = segments.map(seg => {
    const pct = seg.value / total;
    const a1 = angle;
    const a2 = angle + pct * 2 * Math.PI;
    angle = a2;
    if (pct === 0) return null;

    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
    const ix1 = cx + inner * Math.cos(a1), iy1 = cy + inner * Math.sin(a1);
    const ix2 = cx + inner * Math.cos(a2), iy2 = cy + inner * Math.sin(a2);
    const large = pct > 0.5 ? 1 : 0;

    return (
      <path key={seg.label}
        d={`M ${ix1} ${iy1} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${inner} ${inner} 0 ${large} 0 ${ix1} ${iy1} Z`}
        fill={seg.color} opacity="0.9"
      >
        <title>{seg.label}: {seg.value}</title>
      </path>
    );
  });

  return (
    <svg width={size} height={size}>
      {paths}
      <text x={cx} y={cy - 4} textAnchor="middle" fill="var(--text-primary)" fontSize="16" fontWeight="700">{total}</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fill="var(--text-secondary)" fontSize="9">total</text>
    </svg>
  );
}

// ─── KPI Card ───────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, value, label, sub, color, trend }) {
  return (
    <div className="stat-card fade-in">
      <div className="stat-icon" style={{ background: `${color}20`, marginBottom: 10 }}>
        <Icon size={18} color={color} />
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1.1 }}>{value ?? '—'}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{label}</div>
      {sub && (
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 3 }}>
          {trend === 'up' && <ArrowUpRight size={11} color="var(--success)" />}
          {trend === 'down' && <ArrowDownRight size={11} color="var(--danger)" />}
          {sub}
        </div>
      )}
    </div>
  );
}

// ─── Section Card ────────────────────────────────────────────────────────────
function Section({ title, icon: Icon, children, action }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {Icon && <Icon size={16} color="var(--accent)" />}
          <span style={{ fontWeight: 700, fontSize: 14 }}>{title}</span>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function Reports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dash, clientStats, hostStats, monStats, expiring] = await Promise.all([
        api.get('/dashboard').catch(() => ({ data: {} })),
        api.get('/clients/stats').catch(() => ({ data: {} })),
        api.get('/hosting/stats').catch(() => ({ data: {} })),
        api.get('/monitor/stats').catch(() => ({ data: null })),
        api.get('/hosting/expirations', { params: { days: period } }).catch(() => ({ data: [] })),
      ]);
      setData({
        dash: dash.data,
        clients: clientStats.data,
        hosting: hostStats.data,
        monitor: monStats.data,
        expiring: expiring.data || [],
      });
    } catch (e) {
      setError('Eroare la încărcarea rapoartelor. Verificați conexiunea la server.');
      toast.error('Eroare la încărcarea rapoartelor');
    } finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!data?.expiring) return;
    const rows = [
      ['Tip', 'Nume', 'Companie', 'Data Expirare', 'Zile Rămase'],
      ...data.expiring.map(e => {
        const days = Math.ceil((new Date(e.expiry_date) - new Date()) / 86400000);
        return [e.type, e.name, e.company_name || '', e.expiry_date, days];
      })
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'expirari.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="loading-center"><span className="spinner" /></div>;
  if (error) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 16 }}>
      <AlertTriangle size={40} color="var(--warning)" />
      <div style={{ fontSize: 15, color: 'var(--text-secondary)', textAlign: 'center' }}>{error}</div>
      <button className="btn btn-primary" onClick={load}><RefreshCw size={14} /> Reîncearcă</button>
    </div>
  );
  if (!data) return null;

  const { dash, clients, hosting, monitor, expiring } = data;

  // Prepare chart data
  const clientsByCity = clients?.byCity || [];
  const clientStatusData = [
    { status: 'Activi', count: clients?.active || 0 },
    { status: 'Prospecți', count: clients?.prospect || 0 },
    { status: 'Inactivi', count: clients?.inactive || 0 },
  ];

  const expiryByType = expiring.reduce((acc, e) => {
    acc[e.type] = (acc[e.type] || 0) + 1;
    return acc;
  }, {});

  const donutSegments = [
    { label: 'Activi', value: clients?.active || 0, color: 'var(--success)' },
    { label: 'Prospecți', value: clients?.prospect || 0, color: 'var(--accent)' },
    { label: 'Inactivi', value: clients?.inactive || 0, color: 'var(--text-secondary)' },
  ];

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <div className="page-title">Rapoarte & Analiză</div>
          <div className="page-subtitle">Statistici consolidate CRM · Hosting · Monitorizare</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="form-select" style={{ width: 140 }} value={period} onChange={e => setPeriod(parseInt(e.target.value))}>
            <option value={7}>Ultimele 7 zile</option>
            <option value={30}>Ultimele 30 zile</option>
            <option value={60}>Ultimele 60 zile</option>
            <option value={90}>Ultimele 90 zile</option>
          </select>
          <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} /></button>
          <button className="btn btn-secondary" onClick={exportCsv}><Download size={14} /> Export CSV</button>
        </div>
      </div>

      {/* ── KPI Row ── */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <KpiCard icon={Users2} value={clients?.total} label="Total Clienți" sub={`${clients?.active} activi`} color="var(--accent)" trend="up" />
        <KpiCard icon={Server} value={hosting?.vps} label="VPS Active" sub={`${hosting?.monthlyCost?.toFixed(2)} EUR/lună`} color="var(--success)" />
        <KpiCard icon={Globe} value={hosting?.domains} label="Domenii" sub={`${hosting?.ssl} certificate SSL`} color="#8b5cf6" />
        <KpiCard icon={Activity} value={monitor?.up ?? '—'} label="Servere Online" sub={monitor ? `${monitor.avgUptime}% uptime` : 'Modul inactiv'} color={monitor?.down > 0 ? 'var(--danger)' : 'var(--success)'} />
        <KpiCard icon={DollarSign} value={`${(hosting?.monthlyCost || 0).toFixed(0)} €`} label="Cost Lunar Hosting" sub="VPS active" color="var(--warning)" />
        <KpiCard icon={Calendar} value={hosting?.expiring30} label={`Expiră în ${period}z`} sub={`${hosting?.expiring7} critice (<7z)`} color={hosting?.expiring7 > 0 ? 'var(--danger)' : 'var(--warning)'} trend={hosting?.expiring7 > 0 ? 'down' : undefined} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* ── Clienți per Status ── */}
        <Section title="Distribuție Clienți" icon={Users2}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <DonutChart segments={donutSegments} size={130} />
            <div style={{ flex: 1 }}>
              {donutSegments.map(s => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)44' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.color }} />
                    <span style={{ fontSize: 13 }}>{s.label}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: s.color }}>{s.value}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      {clients?.total > 0 ? `${Math.round(s.value / clients.total * 100)}%` : '0%'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* ── Clienți per Oraș ── */}
        <Section title="Top Orașe" icon={TrendingUp}>
          {clientsByCity.length === 0 ? (
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', padding: 20 }}>Date insuficiente</div>
          ) : (
            <BarChart data={clientsByCity} labelKey="city" valueKey="count" color="var(--accent)" height={140} />
          )}
        </Section>

        {/* ── Infrastructură Hosting ── */}
        <Section title="Infrastructură Hosting" icon={Server}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { label: 'VPS Active', value: hosting?.vps, color: 'var(--success)' },
              { label: 'Domenii Active', value: hosting?.domains, color: 'var(--accent)' },
              { label: 'Baze de Date', value: hosting?.databases, color: '#8b5cf6' },
              { label: 'Certificate SSL', value: hosting?.ssl, color: '#06b6d4' },
              { label: 'IP-uri', value: hosting?.ips, color: '#f59e0b' },
              { label: 'Cost Lunar', value: `${(hosting?.monthlyCost || 0).toFixed(2)} €`, color: 'var(--warning)' },
            ].map(item => (
              <div key={item.label} style={{ background: 'var(--bg-hover)', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{item.label}</span>
                <span style={{ fontWeight: 700, fontSize: 16, color: item.color }}>{item.value ?? 0}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Monitor Summary ── */}
        <Section title="Stare Monitorizare" icon={Activity}>
          {!monitor ? (
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', padding: 20 }}>
              Modulul de monitorizare nu este activ sau nu are date.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'Servere monitorizate', value: monitor.total, color: 'var(--accent)' },
                { label: 'Online acum', value: monitor.up, color: 'var(--success)', icon: CheckCircle },
                { label: 'Offline acum', value: monitor.down, color: monitor.down > 0 ? 'var(--danger)' : 'var(--text-secondary)', icon: XCircle },
                { label: 'Uptime mediu 24h', value: `${monitor.avgUptime}%`, color: monitor.avgUptime >= 99 ? 'var(--success)' : 'var(--warning)' },
                { label: 'Răspuns mediu', value: monitor.avgResponse ? `${monitor.avgResponse}ms` : '—', color: 'var(--accent)' },
                { label: 'Incidente deschise', value: monitor.openIncidents, color: monitor.openIncidents > 0 ? 'var(--danger)' : 'var(--success)' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--bg-hover)', borderRadius: 7 }}>
                  <span style={{ fontSize: 13 }}>{item.label}</span>
                  <span style={{ fontWeight: 700, color: item.color }}>{item.value}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* ── Expirări Apropiate ── */}
      <Section title={`Expirări în Următoarele ${period} Zile (${expiring.length})`} icon={Calendar}
        action={<button className="btn btn-secondary btn-sm" onClick={exportCsv}><Download size={12} /> CSV</button>}>
        {expiring.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--success)', fontWeight: 600 }}>
            <CheckCircle size={32} style={{ display: 'block', margin: '0 auto 8px' }} />
            Nicio expirare în această perioadă 🎉
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>TIP</th><th>NUME</th><th>COMPANIE</th><th>DATA EXPIRARE</th><th>ZILE RĂMASE</th></tr>
              </thead>
              <tbody>
                {expiring.map((e, i) => {
                  const days = Math.ceil((new Date(e.expiry_date) - new Date()) / 86400000);
                  const color = days <= 7 ? 'var(--danger)' : days <= 30 ? 'var(--warning)' : 'var(--success)';
                  const typeColors = { domain: '#4f8ef7', vps: '#22c55e', ssl: '#06b6d4', database: '#8b5cf6' };
                  return (
                    <tr key={i}>
                      <td><span className="badge" style={{ background: `${typeColors[e.type]}22`, color: typeColors[e.type] }}>{e.type.toUpperCase()}</span></td>
                      <td style={{ fontWeight: 600, fontSize: 14 }}>{e.name}</td>
                      <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{e.company_name || '—'}</td>
                      <td style={{ fontSize: 13 }}>{new Date(e.expiry_date).toLocaleDateString('ro-RO')}</td>
                      <td><span style={{ fontWeight: 700, color, fontSize: 14 }}>{days <= 0 ? 'EXPIRAT' : `${days} zile`}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* ── Activitate Recentă ── */}
      <Section title="Activitate Recentă" icon={Activity}>
        {!dash?.recentActivity?.length ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', padding: 20 }}>Nicio activitate</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {dash.recentActivity.map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: i < dash.recentActivity.length - 1 ? '1px solid var(--border)33' : 'none' }}>
                <div style={{ width: 30, height: 30, background: 'var(--accent)20', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>
                  {a.full_name?.[0] || '?'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13 }}>{a.details}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                    {a.full_name} · {new Date(a.created_at).toLocaleString('ro-RO')}
                  </div>
                </div>
                <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: 'var(--bg-hover)', color: 'var(--text-secondary)' }}>{a.action}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
